import { useState, useEffect } from 'react';
import { Upload, Eye, Star, ArrowLeft, FileText, Image, Music } from 'lucide-react';
import { submissionApi } from '../services/api';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';

function Submissions() {
  const { user } = useAuthStore();
  const [submissions, setSubmissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [filter, setFilter] = useState('');
  const isStudent = user?.role === 'student';

  // Review state (trainer/admin)
  const [grade, setGrade] = useState('');
  const [score, setScore] = useState('');
  const [feedback, setFeedback] = useState('');
  const [isReviewing, setIsReviewing] = useState(false);

  // Upload state (student)
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadSubmissions();
  }, [filter]);

  const loadSubmissions = async () => {
    try {
      const params = {};
      if (filter) params.status = filter;
      const res = await submissionApi.list(params);
      setSubmissions(res.data);
    } catch (error) {
      toast.error('Failed to load submissions');
    } finally {
      setIsLoading(false);
    }
  };

  const loadSubmissionDetail = async (id) => {
    try {
      const res = await submissionApi.get(id);
      setSelectedSubmission(res.data);
      setGrade(res.data.grade || '');
      setScore(res.data.score || '');
      setFeedback(res.data.reviewer_feedback || '');
    } catch (error) {
      toast.error('Failed to load submission');
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error('Title required'); return; }
    setIsSubmitting(true);
    try {
      await submissionApi.create({ title, description, files });
      toast.success('Work submitted!');
      setShowUploadForm(false);
      setTitle('');
      setDescription('');
      setFiles([]);
      loadSubmissions();
    } catch (error) {
      toast.error('Failed to submit');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReview = async () => {
    if (!selectedSubmission) return;
    setIsReviewing(true);
    try {
      await submissionApi.review(selectedSubmission.id, {
        grade: grade || null,
        score: score ? parseFloat(score) : null,
        feedback,
        status: 'graded'
      });
      toast.success('Submission graded!');
      setSelectedSubmission(null);
      loadSubmissions();
    } catch (error) {
      toast.error('Failed to review');
    } finally {
      setIsReviewing(false);
    }
  };

  const handleDownload = async (fileId) => {
    try {
      const res = await submissionApi.downloadFile(selectedSubmission.id, fileId);
      const file = selectedSubmission.files.find(f => f.id === fileId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file?.original_name || 'file');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'submitted': return 'bg-blue-100 text-blue-700';
      case 'under_review': return 'bg-yellow-100 text-yellow-700';
      case 'graded': return 'bg-green-100 text-green-700';
      case 'returned': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getFileIcon = (type) => {
    if (type === 'image') return Image;
    if (type === 'audio') return Music;
    return FileText;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Detail view
  if (selectedSubmission) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <button onClick={() => setSelectedSubmission(null)} className="flex items-center text-gray-600 hover:text-gray-900 mb-4">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to List
        </button>

        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">{selectedSubmission.title}</h2>
            <span className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusColor(selectedSubmission.status)}`}>
              {selectedSubmission.status.replace('_', ' ')}
            </span>
          </div>

          {selectedSubmission.student_name && (
            <p className="text-sm text-gray-500">By {selectedSubmission.student_name} ({selectedSubmission.student_email})</p>
          )}
          {selectedSubmission.description && (
            <p className="text-gray-600 mt-2">{selectedSubmission.description}</p>
          )}
          <p className="text-xs text-gray-400 mt-2">
            Submitted: {new Date(selectedSubmission.created_at).toLocaleString()}
          </p>

          {/* Files */}
          {selectedSubmission.files?.length > 0 && (
            <div className="mt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Files</h3>
              <div className="space-y-2">
                {selectedSubmission.files.map(file => {
                  const FileIcon = getFileIcon(file.file_type);
                  return (
                    <div key={file.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center">
                        <FileIcon className="w-5 h-5 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-gray-700">{file.original_name}</p>
                          <p className="text-xs text-gray-400">{(file.file_size / 1024).toFixed(1)} KB</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDownload(file.id)}
                        className="text-sm text-primary-600 hover:text-primary-700"
                      >
                        Download
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Feedback display (student view) */}
          {isStudent && selectedSubmission.reviewer_feedback && (
            <div className="mt-6 bg-primary-50 border border-primary-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-primary-800 mb-1">Feedback from {selectedSubmission.reviewer_name}</h3>
              <p className="text-sm text-primary-700">{selectedSubmission.reviewer_feedback}</p>
              {selectedSubmission.grade && (
                <p className="text-sm font-medium text-primary-800 mt-2">Grade: {selectedSubmission.grade}</p>
              )}
              {selectedSubmission.score !== null && (
                <p className="text-sm font-medium text-primary-800">Score: {selectedSubmission.score}</p>
              )}
            </div>
          )}

          {/* Review form (trainer/admin) */}
          {!isStudent && (
            <div className="mt-6 border-t border-gray-200 pt-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Review & Grade</h3>
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Grade</label>
                  <input
                    type="text"
                    value={grade}
                    onChange={(e) => setGrade(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="e.g., A+, Pass, Good"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Score</label>
                  <input
                    type="number"
                    value={score}
                    onChange={(e) => setScore(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="e.g., 85"
                  />
                </div>
              </div>
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-600 mb-1">Feedback</label>
                <textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="Provide feedback to the student..."
                />
              </div>
              <button
                onClick={handleReview}
                disabled={isReviewing}
                className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50"
              >
                {isReviewing ? 'Saving...' : 'Grade & Return'}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isStudent ? 'My Submissions' : 'Student Submissions'}
          </h1>
          <p className="text-gray-600 mt-1">
            {isStudent ? 'Upload and track your work submissions' : 'Review and grade student submissions'}
          </p>
        </div>
        {isStudent && (
          <button
            onClick={() => setShowUploadForm(!showUploadForm)}
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <Upload className="w-4 h-4 mr-2" />
            New Submission
          </button>
        )}
      </div>

      {/* Upload Form */}
      {showUploadForm && isStudent && (
        <form onSubmit={handleUpload} className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">Submit Your Work</h3>
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="Submission title"
            />
          </div>
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Files</label>
            <input
              type="file"
              multiple
              accept="image/*,.pdf,audio/*"
              onChange={(e) => setFiles(Array.from(e.target.files))}
              className="w-full text-sm"
            />
          </div>
          <div className="flex space-x-2">
            <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50">
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
            <button type="button" onClick={() => setShowUploadForm(false)} className="px-4 py-2 border border-gray-300 rounded-lg">
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Filters */}
      {!isStudent && (
        <div className="flex space-x-2 mb-4">
          {['', 'submitted', 'under_review', 'graded', 'returned'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-sm rounded-lg ${
                filter === f ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f ? f.replace('_', ' ').replace(/^\w/, c => c.toUpperCase()) : 'All'}
            </button>
          ))}
        </div>
      )}

      {/* Submissions list */}
      {submissions.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Upload className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <p>No submissions yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {submissions.map(sub => (
            <div
              key={sub.id}
              onClick={() => loadSubmissionDetail(sub.id)}
              className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg cursor-pointer hover:border-primary-300 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-gray-900 truncate">{sub.title}</h3>
                <div className="flex items-center space-x-3 mt-1 text-sm text-gray-500">
                  {!isStudent && <span>{sub.student_name}</span>}
                  {sub.lesson_title && <span>{sub.lesson_title}</span>}
                  <span>{new Date(sub.created_at).toLocaleDateString()}</span>
                  <span>{sub.file_count} file{sub.file_count !== 1 ? 's' : ''}</span>
                </div>
              </div>
              <div className="flex items-center space-x-3 ml-4">
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusColor(sub.status)}`}>
                  {sub.status.replace('_', ' ')}
                </span>
                {sub.score !== null && (
                  <span className="text-sm font-medium text-gray-700">{sub.score}</span>
                )}
                <Eye className="w-4 h-4 text-gray-400" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Submissions;
